import React, { useEffect, FC, useRef } from 'react';
import useRuntimeConfig from '@vl-core/hooks/useConfig';

declare global {
  interface Window {
    AwsWafCaptcha: {
      renderCaptcha: (
        container: Element,
        configuration: {
          apiKey: string;
          onSuccess: (wafToken: string) => void;
          onError?: (
            error: Error & {
              kind: 'internal_error' | 'network_error' | 'token_error' | 'client_error';
              statusCode?: number;
            }
          ) => void;
          onLoad?: () => void;
          onPuzzleTimeout?: () => void;
          onPuzzleCorrect?: () => void;
          onPuzzleIncorrect?: () => void;
          defaultLocale?: string;
          disableLanguageSelector?: boolean;
          dynamicWidth?: boolean;
          skipTitle?: boolean;
        }
      ) => void;
    };
  }
}

const WAF_INTEGRATION_ID = '172c582a9c09';
const WAF_SCRIPT_SRC = `https://${WAF_INTEGRATION_ID}.edge.captcha-sdk.awswaf.com/${WAF_INTEGRATION_ID}/jsapi.js`;

export const AwsWafScript = () => {
  useEffect(() => {
    if (document.querySelector(`script[src="${WAF_SCRIPT_SRC}"]`)) return;

    const script = document.createElement('script');
    script.src = WAF_SCRIPT_SRC;
    script.async = true;
    document.body.appendChild(script);
  }, []);

  return null;
};

type WidgetProps = {
  onSuccess: (wafToken: string) => void;
  onError?: (
    error: Error & {
      kind: 'internal_error' | 'network_error' | 'token_error' | 'client_error';
      statusCode?: number;
    }
  ) => void;
};

const AwsWafCaptchaWidget: FC<WidgetProps> = ({ onSuccess, onError }) => {
  const { CF_KEY } = useRuntimeConfig();
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!window.AwsWafCaptcha || !containerRef.current) return;

    window.AwsWafCaptcha.renderCaptcha(containerRef.current, {
      apiKey: CF_KEY,
      skipTitle: true,
      disableLanguageSelector: true,
      onSuccess: (token) => {
        onSuccess(token);
      },
      onError: (error) => {
        onError?.(error);
      }
    });
  }, []);

  return <div ref={containerRef} />;
};

export default AwsWafCaptchaWidget;
